import { RichTextRenderer } from '@contember/react-client'
import Image from 'next/image'
import Link from 'next/link'
import type { FunctionComponent } from 'react'
import type { PageProps } from '../pages/[[...path]]'
import { contemberLinkToHref, contemberLinkToHrefTargetRel } from '../utilities/contemberLinkToHref'
import { Container } from './Container'
import { useSite } from './contexts/SiteContextProvider'
import styles from './Footer.module.sass'
import { SocialLinks } from './SocialLinks'
import clsx from 'clsx'
import { useMemo } from 'react'
import { headerLinkToHrefTargetRel } from '../utilities/headerLinkToHref'
import { isDefined } from '../utilities/isDefined'
import { useTranslate } from './contexts/TranslationsContextProvider'
import { Button } from './Button'

export type FooterProps = NonNullable<PageProps['footer']> & Record<string, unknown>

export const Footer: FunctionComponent<FooterProps> = (props) => {
	// Cast to any to accommodate additional fields coming from the Website schema
	const {
		logo,
		localesByLocale,
		contactPageLocaleData,
		careerPageLocaleData,
		listBranch,
		jobPositionCount,
		socialLinksList,
		// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	} = props as any

	const translation = useTranslate()
	const { baseUrl } = useSite()

	const columns = localesByLocale?.links
	const socialLinks = socialLinksList ?? localesByLocale?.socialLinks

	const copyRight = localesByLocale?.copyright
	const copyRightSecondary = localesByLocale?.copyrightSecondary
	const copyrightTertiary = localesByLocale?.copyrightTertiary
	return (
		<div className={styles.wrapper}>
			<Container size="wide">
				<div className={styles.main}>
					<div className={styles.columnMain}>
						{logo && (
							<Link className={styles.logo} href={baseUrl}>
								<Image
									className={styles.logoImage}
									src={logo.url}
									width={logo.width}
									height={logo.height}
									alt={logo.alt ?? ''}
								/>
							</Link>
						)}
						{contactPageLocaleData?.link && (
							<div className={styles.button}>
								<Button type="link" size="small" href={contactPageLocaleData.link.url}>
									{translation('footer.contactLabel')}
								</Button>
							</div>
						)}
					</div>
					{columns && (
						<Columns
							columns={columns}
							jobPositionCount={jobPositionCount}
							careerPageLocaleData={careerPageLocaleData}
							listBranch={listBranch}
						/>
					)}
				</div>
				<div className={styles.bottomBar}>
					{socialLinks && <SocialLinks items={socialLinks.items} />}
					<div className={styles.copyrights}>
						{copyRight && <div className={styles.copyright}>{copyRight}</div>}
						{copyRightSecondary && (
							<div className={styles.copyright}>
								<RichTextRenderer source={copyRightSecondary} />
							</div>
						)}
						{copyrightTertiary && (
							<div className={styles.copyright}>
								{/* biome-ignore lint/a11y/useValidAnchor: <explanation> */}
								<a href="" data-cc="show-preferencesModal">
									{copyrightTertiary} <div />
								</a>
							</div>
						)}
					</div>
				</div>
			</Container>
		</div>
	)
}

type ColumnsProps = {
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	columns: any[]
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	listBranch: any[] | null
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	careerPageLocaleData: any | null
	jobPositionCount: number | null
}

const BRANCH_ITEMS_PER_COLUMN = 6

const Columns: FunctionComponent<ColumnsProps> = ({ columns, listBranch, careerPageLocaleData, jobPositionCount }) => {
	const translation = useTranslate()

	const branchArray = useMemo(() => {
		if (!listBranch) {
			return []
		}

		const array: Array<ColumnsProps['listBranch']> = []

		for (let i = 0; i < listBranch.length; i += BRANCH_ITEMS_PER_COLUMN) {
			array.push(listBranch.slice(i, i + BRANCH_ITEMS_PER_COLUMN))
		}

		return array.filter(isDefined)
	}, [listBranch])

	return (
		<div className={styles.columns}>
			{columns.map((column) => (
				<div key={column.id} className={styles.column}>
					{column.title && <h4 className={styles.columnTitle}>{column.title}</h4>}
					<ul className={styles.columnList}>
						{column.items.map(
							(item) =>
								item.link && (
									<li key={item.id} className={styles.columnItem}>
										<Link className={styles.columnItemLink} {...headerLinkToHrefTargetRel(item.link)}>
											{item.link.title}
										</Link>
										{(jobPositionCount ?? 0) > 0 &&
											careerPageLocaleData?.link?.url === contemberLinkToHrefTargetRel(item.link).href && (
												<span className={styles.columnItemLabel}>{translation('footer.column.label.careers')}</span>
											)}
									</li>
								),
						)}
					</ul>
				</div>
			))}
			{branchArray && branchArray.length > 0 && (
				<div className={clsx(styles.column, styles.is_branch)}>
					<h4 className={styles.columnTitle}>{translation('footer.column.branchesLabel')}</h4>
					<div className={styles.columnsList}>
						{branchArray.map(
							(column, index) =>
								column.length > 0 && (
									<ul key={index} className={styles.columnList}>
										{column.length > 0 &&
											column.map(
												(item) =>
													item.link && (
														<li key={item.id} className={styles.columnItem}>
															<Link className={styles.columnItemLink} href={contemberLinkToHref(item.link)}>
																{item.market?.localesByLocale?.name}
															</Link>
														</li>
													),
											)}
									</ul>
								),
						)}
					</div>
				</div>
			)}
		</div>
	)
}
